import { useState, useEffect } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, ROLES } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { Badge, LoadingSpinner, SectionHeading, PageEmpty } from '../../../shared/components/UI'
import { mmApi, announcementsApi } from '../../../shared/lib/api'

const TASK_ROUTE = { 'pending': '/app/cutting', 'in_progress': '/app/bobbin' }
const STATUS_BADGE = {
  created: { v: 'blue', l: 'Created' },
  pending: { v: 'amber', l: 'Pending' },
  in_progress: { v: 'amber', l: 'In Progress' },
  completed: { v: 'green', l: 'Completed' }
}

export default function DashboardPage() {
  const { user }  = useAuthStore()
  const navigate  = useNavigate()
  const isMM      = user?.role === ROLES.METALLIC_MASTER
  const isTeal    = !isMM

  const [loading, setLoading] = useState(true)
  const [data, setData]       = useState({ announcements: [], items: [] })

  const fetchDashboard = async () => {
    try {
      const [annRes, mmRes] = await Promise.all([
        announcementsApi.listForUser(),
        mmApi.dashboard()
      ])
      setData({
        announcements: annRes.data.data || [],
        items: mmRes.data.data || []
      })
    } catch (err) {
      console.error('Failed to fetch dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
    // Automatic refresh every 30 seconds
    const interval = setInterval(fetchDashboard, 30000)
    return () => clearInterval(interval)
  }, [])

  if (loading) return <TabletScreen><div className="p-12 text-center"><LoadingSpinner /></div></TabletScreen>

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <TabletScreen>
      <TabletHeader
        title={`${greeting}, ${user?.name?.split(' ')[0] || 'Supervisor'}`}
        role={isMM ? 'Metallic Master' : 'Loading Supervisor'}
        unit={user?.unitName || 'Unit'}
        teal={isTeal}
      />
      <div className="p-4">

        {/* Announcement banner */}
        {(data.announcements || []).map(a => (
          <div key={a.id} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
            <p className="text-xs font-semibold text-amber-700 mb-0.5">📢 Announcement</p>
            <p className="text-sm font-medium text-amber-800">{a.title}</p>
            <p className="text-xs text-amber-600 mt-0.5">{a.message}</p>
          </div>
        ))}

        {/* Quick actions */}
        <SectionHeading>Quick actions</SectionHeading>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button 
            onClick={() => navigate('/app/cutting')} 
            className="bg-brand-600 py-5 rounded-2xl text-base font-bold text-white hover:bg-brand-700 flex flex-col items-center justify-center gap-1 shadow-lg shadow-brand-100"
          >
            <span className="text-2xl">✂️</span>
            Cut Roll
          </button>
          <button 
            onClick={() => navigate('/app/bobbin')}  
            className="bg-white py-5 rounded-2xl text-base font-bold text-gray-800 border border-gray-100 hover:bg-gray-50 flex flex-col items-center justify-center gap-1 shadow-sm"
          >
            <span className="text-2xl">🧵</span>
            Create Batch
          </button>
        </div>

        {/* Ongoing Processes */}
        <div className="flex items-center justify-between mb-3">
          <SectionHeading className="!mb-0">Ongoing Processes</SectionHeading>
          <Badge variant="blue">{data.items.filter(i => i.color !== 'green').length}</Badge>
        </div>

        {data.items.length === 0 ? (
          <PageEmpty message="No active processes" />
        ) : (
          <div className="grid grid-cols-1 gap-3 mb-8">
            {data.items.map(item => {
              const rate = item.original?.rate_per_kg || item.original?.rate
              const status = STATUS_BADGE[item.statusLabel?.toLowerCase()] || { v: 'gray', l: item.statusLabel || 'Unknown' }
              
              return (
                <div 
                  key={item.id}
                  onClick={() => navigate(TASK_ROUTE[item.statusLabel?.toLowerCase()] || `/app/dashboard`)}
                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center gap-4">
                    <div className={clsx(
                      "w-12 h-12 rounded-xl flex items-center justify-center text-xl",
                      item.color === 'yellow' ? "bg-amber-100 text-amber-600" :
                      item.color === 'blue' ? "bg-blue-100 text-blue-600" :
                      item.color === 'red' ? "bg-red-100 text-red-600" :
                      item.color === 'green' ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"
                    )}>
                      {item.type === 'roll' ? '🧻' : '📄'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{item.code}</p>
                      <p className="text-xs text-gray-500 font-medium">{item.shade} · {item.weight?.toFixed(2)} KG</p>
                      {rate && (
                        <p className="text-[10px] font-extrabold text-brand-600 mt-0.5">₹ {parseFloat(rate).toFixed(2)} / KG</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={status.v}>{status.l}</Badge>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Live Inventory */}
        <div className="flex items-center justify-between mb-3 mt-4">
          <SectionHeading className="!mb-0">Live Inventory</SectionHeading>
          <Badge variant="teal">{data.items.filter(i => i.type === 'roll' && i.color !== 'red').length}</Badge>
        </div>

        {data.items.filter(i => i.type === 'roll').length === 0 ? (
          <PageEmpty message="No items in inventory" />
        ) : (
          <div className="grid grid-cols-2 gap-3 mb-12">
            {data.items.filter(i => i.type === 'roll').map(item => {
              const rate = item.original?.rate_per_kg || item.original?.rate
              return (
                <div 
                  key={item.id}
                  className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={item.color === 'red' ? 'red' : 'teal'}>{item.code}</Badge>
                  </div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{item.shade}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-lg font-black text-gray-900">{item.weight?.toFixed(2)}</span>
                    <span className="text-[10px] font-bold text-gray-400">KG</span>
                  </div>
                  {rate && (
                    <p className="text-[10px] font-extrabold text-brand-600 mt-1">₹ {parseFloat(rate).toFixed(2)}</p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </TabletScreen>
  )
}
